import { z } from 'zod'
import {
  digitsOnlyPhone,
  isValidPropertyPhoneDigits,
  isValidOptionalPropertyPhoneDigits,
} from './formatPhone'

const ZIPCODE_FORMAT_ERROR =
  'Enter Valid Zipcode, Should match pattern [XXXXX] or [XXXXX-XXXX].'

/** Matches Angular `zipcodeValidator` (`customvalidation.ts`): non-empty value must have length 5 or 9 (no trim). */
export function isZipcodeValidAngularStyle(val: string): boolean {
  return val.length === 5 || val.length === 9
}

/** API + RHF may use numbers on fields we treat as strings. */
function mixedToFormString() {
  return z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v) => (v === undefined || v === null ? '' : String(v)))
}

export const propertyAddSchema = z.object({
  fk_company_id: z.union([z.string().min(1, 'Company Selection is required.'), z.number()]),
  property_name: z.string().min(1, 'Property Name is required.'),
  contact_name: z.string().min(1, 'Primary Contact is required.'),
  contact_phone: z
    .string()
    .min(1, 'Primary Phone is required.')
    .refine(
      (val) => isValidPropertyPhoneDigits(digitsOnlyPhone(val)),
      'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
    ),
  property_office_phone: z
    .string()
    .optional()
    .refine(
      (val) => isValidOptionalPropertyPhoneDigits(digitsOnlyPhone(val ?? '')),
      'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
    ),
  property_address: z.string().min(1, 'Address Line1 is required.'),
  property_address2: z.string().optional(),
  property_city: z.string().min(1, 'City is required.'),
  property_state: z.string().min(1, 'State is required.'),
  property_zipcode: z
    .string()
    .min(1, 'Zipcode is required.')
    .refine((val) => isZipcodeValidAngularStyle(val), ZIPCODE_FORMAT_ERROR),
  units_count: z.union([
    z.string().min(1, 'Units is required.'),
    z.number(),
  ]),
  listed: z.string().min(1, 'Liberty Reference Selection is required.'),
  listed_ids: z.array(z.union([z.string(), z.number()])).optional(),
  latitude: z.union([z.string(), z.number()]).optional(),
  longitude: z.union([z.string(), z.number()]).optional(),
})

/**
 * Edit schema – mirrors Angular propertyEditForm.
 * Differences from Add: contact_name is optional, adds alias, ALN, mailing address fields.
 */
export const propertyEditSchema = z
  .object({
    fk_company_id: z.union([z.string().min(1, 'Company Selection is required.'), z.number()]),
    property_name: mixedToFormString().pipe(z.string().min(1, 'Property Name is required.')),
    property_alias_name: mixedToFormString(),
    aln_ref_id: mixedToFormString(),
    contact_name: mixedToFormString(),
    contact_phone: mixedToFormString().pipe(
      z.string().refine(
        (val) => isValidOptionalPropertyPhoneDigits(digitsOnlyPhone(val)),
        'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
      )
    ),
    property_office_phone: mixedToFormString().pipe(
      z.string().refine(
        (val) => isValidOptionalPropertyPhoneDigits(digitsOnlyPhone(val)),
        'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'
      )
    ),
    property_address: mixedToFormString().pipe(z.string().min(1, 'Address Line1 is required.')),
    property_address2: mixedToFormString(),
    property_city: mixedToFormString().pipe(z.string().min(1, 'City is required.')),
    property_state: mixedToFormString().pipe(z.string().min(1, 'State is required.')),
    property_zipcode: mixedToFormString().pipe(
      z
        .string()
        .min(1, 'Zipcode is required.')
        .refine((val) => isZipcodeValidAngularStyle(val), ZIPCODE_FORMAT_ERROR)
    ),
    is_mailing_address: z.boolean().optional(),
    mailing_property_address: mixedToFormString(),
    mailing_property_address2: mixedToFormString(),
    mailing_property_city: mixedToFormString(),
    mailing_property_state: mixedToFormString(),
    mailing_property_zipcode: mixedToFormString().pipe(
      z
        .string()
        .refine(
          (val) => !val || isZipcodeValidAngularStyle(val),
          ZIPCODE_FORMAT_ERROR,
        )
    ),
    units_count: z.union([
      z.string().min(1, 'Units is required.'),
      z.number(),
    ]),
    listed: mixedToFormString().pipe(
      z.string().min(1, 'Liberty Reference Selection is required.')
    ),
    listed_ids: z.array(z.union([z.string(), z.number()])).optional(),
    occupancy: z.union([z.string(), z.number()]).optional(),
    rent_amount: mixedToFormString(),
    federal_id: mixedToFormString(),
    pmc_invite: mixedToFormString(),
    main_contact_person: mixedToFormString(),
    policy_id: mixedToFormString(),
    website_url: mixedToFormString(),
    latitude: z.union([z.string(), z.number()]).optional(),
    longitude: z.union([z.string(), z.number()]).optional(),
    email_frequency: mixedToFormString().pipe(
      z.string().min(1, 'Reports Reminder Frequency is required.')
    ),
    activate_api: mixedToFormString(),
    disable_uc: mixedToFormString(),
    range: mixedToFormString(),
    /** Access fee radios live on Config in React but must mirror Angular: same value as main form on Submit. */
    allow_dynamic_access_fee: z.union([z.literal(0), z.literal(1)]),
  })
  .superRefine((data, ctx) => {
    if (data.is_mailing_address) {
      if (!data.mailing_property_address) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mailing Address is required.', path: ['mailing_property_address'] })
      }
      if (!data.mailing_property_city) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mailing City is required.', path: ['mailing_property_city'] })
      }
      if (!data.mailing_property_state) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mailing State is required.', path: ['mailing_property_state'] })
      }
      if (!data.mailing_property_zipcode) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mailing Zipcode is required.', path: ['mailing_property_zipcode'] })
      } else if (!isZipcodeValidAngularStyle(data.mailing_property_zipcode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ZIPCODE_FORMAT_ERROR,
          path: ['mailing_property_zipcode'],
        })
      }
    }
  })

